import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useOverviewWs } from '../lib/ws';
import clsx from '../lib/clsx';

const NAV = [
  { to: '/', label: 'Overview', icon: IconGrid },
  { to: '/agents', label: 'Agentes', icon: IconServer },
  { to: '/sites', label: 'Sites / SSL', icon: IconGlobe },
  { to: '/alerts', label: 'Alertas', icon: IconBell },
  { to: '/events', label: 'Eventos', icon: IconList },
  { to: '/settings', label: 'Configurações', icon: IconGear },
];

export function Layout() {
  const { logout } = useAuth();
  const location = useLocation();
  const { connected } = useOverviewWs();
  return (
    <div className="min-h-screen flex">
      <aside className="w-64 bg-bg-subtle border-r border-border flex flex-col p-4 sticky top-0 h-screen">
        <div className="flex items-center gap-3 px-2 py-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-accent to-info shadow-lg shadow-accent/30 grid place-items-center font-bold text-bg">F</div>
          <div>
            <div className="font-bold tracking-wide text-white">FreeBuff</div>
            <div className="text-[11px] text-slate-400">Painel de VPS</div>
          </div>
        </div>
        <nav className="flex-1 flex flex-col gap-1">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) => clsx('nav-link', isActive && 'active')}
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="mt-4 pt-4 border-t border-border space-y-3">
          <div className={clsx('pill pill-muted text-xs px-2.5 py-1')}>
            <span className={clsx('w-2 h-2 rounded-full', connected ? 'bg-ok animate-pulseSoft' : 'bg-slate-500')}></span>
            <span>{connected ? 'Ao vivo' : 'Reconectando'}</span>
          </div>
          <button onClick={() => logout()} className="btn w-full justify-center">Sair</button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 p-6 lg:p-8">
        <Header />
        <div className="animate-fadeIn" key={location.pathname}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function Header() {
  const loc = useLocation();
  const titles: Record<string, string> = {
    '/': 'Overview',
    '/agents': 'Agentes',
    '/sites': 'Sites / SSL',
    '/alerts': 'Regras de alertas',
    '/events': 'Histórico de eventos',
    '/settings': 'Configurações',
  };
  let title = titles[loc.pathname] ?? '—';
  if (loc.pathname.startsWith('/agents/')) title = 'Detalhes do agente';
  return (
    <div className="mb-6 flex items-baseline justify-between">
      <h1 className="text-2xl font-bold tracking-tight text-white">{title}</h1>
      <span className="text-xs text-slate-500 font-mono">{loc.pathname}</span>
    </div>
  );
}

// === Inline icons
function IconGrid(props: { className?: string }) { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={props.className}><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/></svg>; }
function IconServer(props: { className?: string }) { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={props.className}><rect x="3" y="3" width="18" height="7" rx="2"/><rect x="3" y="14" width="18" height="7" rx="2"/><circle cx="7" cy="6.5" r="0.8" fill="currentColor"/><circle cx="7" cy="17.5" r="0.8" fill="currentColor"/></svg>; }
function IconGlobe(props: { className?: string }) { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={props.className}><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/></svg>; }
function IconBell(props: { className?: string }) { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={props.className}><path d="M6 8a6 6 0 1 1 12 0c0 5 2 6 2 6H4s2-1 2-6Z"/><path d="M10 21a2 2 0 0 0 4 0"/></svg>; }
function IconList(props: { className?: string }) { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={props.className}><path d="M8 6h13M8 12h13M8 18h13"/><circle cx="4" cy="6" r="1" fill="currentColor"/><circle cx="4" cy="12" r="1" fill="currentColor"/><circle cx="4" cy="18" r="1" fill="currentColor"/></svg>; }
function IconGear(props: { className?: string }) { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={props.className}><path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3h.1a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z"/></svg>; }
